import type { ConversationSummary, Notification } from "@teamna/shared";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  AlertIcon,
  Badge,
  Button,
  Card,
  ChatIcon,
  ChipRow,
  ConversationRow,
  EmptyState,
  ErrorState,
  MegaphoneIcon,
  SkeletonList,
  type ChipOption,
} from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { forBusiness, useConversations } from "@teamna/web-client/hooks/use-messaging.ts";
import {
  newestScheduledFor,
  ownerFacing,
  useMarkNotificationsRead,
  useNotifications,
} from "@teamna/web-client/hooks/use-notifications.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { formatCountdown } from "@teamna/web-client/lib/format.ts";
import { useIsDesktop } from "../lib/media.ts";
import { AnnouncePanel } from "./announce.tsx";
import { OfficePage } from "./office.tsx";
import { OwnerGate } from "./shell.tsx";
import { OwnerThreadPage, ThreadBody } from "./thread.tsx";

/**
 * Everything that reached the business without the owner doing it.
 *
 * Two segments under one tab. Messages is what members wrote and what the business posted; Activity is
 * the cancellation feed from round one, unchanged. They share a tab because five is the ceiling
 * for a thumb-reachable bar, messaging is the daily action that needs the badge, and both are the
 * same kind of thing: news, not controls. The badge on the tab is the sum of the two.
 *
 * Messages opens first. A cancellation is one event with a deadline; a message is a person waiting
 * for an answer, and that is the one an owner opening the app wants to see.
 */

type Segment = "messages" | "activity";

/**
 * The inbox at every address it has: `/owner/inbox`, `/owner/inbox/$conversationId` and
 * `/owner/inbox/announce`.
 *
 * At 1024px and up it is two panes: the list on the left, the open thread on the right, so an
 * owner answers five parents without losing their place. Below that it is one screen at a time:
 * the list, or the thread with a back link, as on the phone. The announcement composer opens as a
 * Panel over whichever of the two is showing.
 */
export function InboxFrame({
  conversationId,
  composing = false,
}: {
  conversationId: string | null;
  composing?: boolean;
}) {
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  // The front desk (T1) does not post announcements.
  const { may } = useActiveBusiness();
  const composer = composing ? (
    <AnnouncePanel onClose={() => void navigate({ to: "/owner/inbox" })} />
  ) : null;

  if (!desktop) {
    return (
      <>
        {conversationId ? <OwnerThreadPage conversationId={conversationId} /> : <OwnerInboxList />}
        {composer}
      </>
    );
  }

  return (
    <>
      <OfficePage
        title="Inbox"
        actions={
          may("announce") ? (
            <Button variant="secondary" onClick={() => void navigate({ to: "/owner/inbox/announce" })}>
              <MegaphoneIcon size={18} />
              New announcement
            </Button>
          ) : null
        }
      >
        {(business) => <TwoPanes businessId={business.businessId} conversationId={conversationId} />}
      </OfficePage>
      {composer}
    </>
  );
}

function TwoPanes({ businessId, conversationId }: { businessId: string; conversationId: string | null }) {
  const [segment, setSegment] = useState<Segment>("messages");
  const conversations = useConversations();
  const row = (conversations.data ?? []).find((candidate) => candidate.id === conversationId) ?? null;

  return (
    <div className="grid h-[calc(100dvh-10rem)] min-h-[480px] grid-cols-[340px_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-paper shadow-card">
      <section aria-label="Conversations" className="flex min-h-0 flex-col border-r border-line">
        <div className="border-b border-line px-4 py-3">
          <SegmentControl value={segment} onChange={setSegment} />
        </div>
        <div
          role="tabpanel"
          id={`inbox-panel-${segment}`}
          aria-labelledby={`inbox-tab-${segment}`}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {segment === "messages" ? (
            <MessagesSegment businessId={businessId} selectedId={conversationId} flush />
          ) : (
            <div className="p-3">
              <OwnerActivityFeed businessId={businessId} />
            </div>
          )}
        </div>
      </section>

      {/*
       * This pane scrolls on its own, with no tab bar, so the thread's dock sticks to its bottom
       * edge and the thread fills the pane (`--thread-chrome` cancels its minimum height). No
       * bottom padding here: the browser measures `sticky` from the content box, so padding under
       * the thread would leave a gap between the composer and the pane's edge.
       */}
      <section
        aria-label="Conversation"
        className="flex min-h-0 flex-col overflow-y-auto px-5 pt-4 [--safe-bottom:0px] [--screen-pad-bottom:0px] [--tabbar-height:0px] [--thread-chrome:100dvh]"
      >
        {conversationId === null ? (
          <div className="m-auto max-w-[360px]">
            <EmptyState
              icon={<ChatIcon size={30} />}
              title="Pick a conversation"
              message="Open a thread on the left to read it and reply here."
            />
          </div>
        ) : row ? (
          <>
            <h2 className="pb-1 text-[21px]">
              {row.kind === "announcement" ? "Announcements" : row.counterpartName}
            </h2>
            <ThreadBody row={row} />
          </>
        ) : conversations.isLoading ? (
          <SkeletonList rows={5} />
        ) : (
          <ErrorState
            error={conversations.error ?? { code: "CONVERSATION_NOT_FOUND" }}
            onRetry={() => void conversations.refetch()}
          />
        )}
      </section>
    </div>
  );
}

/** The inbox list below 1024px: one screen, as on the phone. */
function OwnerInboxList() {
  const { business, managed, isLoading, error, select } = useActiveBusiness();
  const businessId = business?.businessId ?? null;
  const [segment, setSegment] = useState<Segment>("messages");

  const chips = useMemo<ChipOption[]>(
    () => managed.map((row) => ({ id: row.businessId, label: row.name })),
    [managed],
  );

  return (
    <OwnerGate
      title="Inbox"
      business={business}
      isLoading={isLoading}
      error={error}
      managedCount={managed.length}
    >
      {chips.length > 1 ? (
        <div className="pb-4">
          <ChipRow
            options={chips}
            selectedId={business?.businessId ?? ""}
            onSelect={select}
            label="Which business to manage"
          />
        </div>
      ) : null}

      <SegmentControl value={segment} onChange={setSegment} />

      <div
        role="tabpanel"
        id={`inbox-panel-${segment}`}
        aria-labelledby={`inbox-tab-${segment}`}
        className="pt-4"
      >
        {segment === "messages" ? (
          <MessagesSegment businessId={businessId} selectedId={null} />
        ) : (
          <OwnerActivityFeed businessId={businessId} />
        )}
      </div>
    </OwnerGate>
  );
}

/** `/owner/inbox` */
export function OwnerInboxScreen() {
  return <InboxFrame conversationId={null} />;
}

/** `/owner/inbox/$conversationId` */
export function OwnerThreadScreen() {
  const { conversationId } = useParams({ from: "/owner/inbox/$conversationId" });
  return <InboxFrame conversationId={conversationId} />;
}

/** `/owner/inbox/announce`: the list with the composer Panel open over it. */
export function OwnerAnnounceScreen() {
  return <InboxFrame conversationId={null} composing />;
}

/**
 * The two segments, as a tab list.
 *
 * The same silhouette as the business switcher above it, because it is the same gesture: choose one of
 * a few views. Real tab semantics rather than `aria-pressed`, since the panel below swaps entirely.
 */
function SegmentControl({
  value,
  onChange,
}: {
  value: Segment;
  onChange: (next: Segment) => void;
}) {
  const options: Array<{ id: Segment; label: string }> = [
    { id: "messages", label: "Messages" },
    { id: "activity", label: "Activity" },
  ];

  return (
    <div role="tablist" aria-label="What to read" className="flex gap-2">
      {options.map((option) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            id={`inbox-tab-${option.id}`}
            aria-selected={active}
            aria-controls={`inbox-panel-${option.id}`}
            onClick={() => onChange(option.id)}
            className={[
              "inline-flex h-9 items-center rounded-pill border px-3.5 text-[13px] font-semibold whitespace-nowrap transition-colors duration-150",
              active ? "sk-chip-on" : "sk-chip text-ink-2 active:brightness-95",
            ].join(" ")}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// --- messages --------------------------------------------------------------------------

/**
 * The business's threads, one row each, with the announcement channel pinned on top.
 *
 * The API already answers the staff view for a managed business, so the only filter here is the business
 * itself: an owner of two businesses sees one inbox per business, switched by the chips above.
 */
function MessagesSegment({
  businessId,
  selectedId,
  flush = false,
}: {
  businessId: string | null;
  selectedId: string | null;
  /** In the desktop list pane: no announcement button (it is in the page header) and no card frame. */
  flush?: boolean;
}) {
  const navigate = useNavigate();
  const conversations = useConversations();
  const { label } = useLabels();
  const { may } = useActiveBusiness();

  const rows = useMemo(() => {
    const scoped = forBusiness(conversations.data ?? [], businessId);
    const channel = scoped.filter((row) => row.kind === "announcement");
    const direct = scoped.filter((row) => row.kind !== "announcement");
    return [...channel, ...direct];
  }, [conversations.data, businessId]);

  // A channel with nothing posted is not a conversation yet, so the inbox is still empty.
  const quiet = rows.every((row) => row.kind === "announcement" && row.lastMessage === null);

  function open(row: ConversationSummary) {
    void navigate({ to: "/owner/inbox/$conversationId", params: { conversationId: row.id } });
  }

  const list = rows.map((row, index) => (
    <ConversationRow
      key={row.id}
      row={row}
      title={row.kind === "announcement" ? "Announcements" : row.counterpartName}
      onSelect={() => open(row)}
      selected={row.id === selectedId}
      last={index === rows.length - 1 && !flush}
    />
  ));

  return (
    <div className={flush ? "flex flex-col" : "flex flex-col gap-4"}>
      {flush || !may("announce") ? null : (
        <Button
          variant="secondary"
          fullWidth
          onClick={() => void navigate({ to: "/owner/inbox/announce" })}
        >
          <MegaphoneIcon size={18} />
          New announcement
        </Button>
      )}

      {conversations.isLoading && !conversations.data ? (
        <SkeletonList />
      ) : conversations.error && !conversations.data ? (
        <ErrorState error={conversations.error} onRetry={() => void conversations.refetch()} />
      ) : quiet ? (
        <EmptyState
          icon={<ChatIcon size={30} />}
          title="No messages yet"
          message={`${label("member", { plural: true, capital: true })} can message you from their Inbox, and you can start a thread from any ${label("member")} or from a session's list.`}
        />
      ) : flush ? (
        list
      ) : (
        <Card>{list}</Card>
      )}
    </div>
  );
}

// --- activity --------------------------------------------------------------------------

/**
 * What has happened at the business without the owner doing it.
 *
 * Today that is one thing: a member cancelled. It earns a segment anyway, because it is the one
 * member action with a deadline attached. A spot that frees up on Monday for a Thursday session
 * can still be offered to somebody; the same spot found on Thursday evening is a spot that went
 * empty, and the difference is entirely whether the owner heard about it.
 *
 * The rows are notifications, the same ones the outbox would push to a phone, read back from the
 * feed rather than mirrored into a second table. One event, one record, whichever surface shows
 * it: an owner who saw the push and an owner who only ever opens the web app are looking at the
 * same row, and marking it read here marks it read everywhere.
 *
 * Opening the segment marks the feed read up to the newest row on it. Anything that arrives while
 * it is open stays unread, so the badge cannot swallow something that was never actually seen.
 */
function OwnerActivityFeed({ businessId }: { businessId: string | null }) {
  const { label } = useLabels();
  const notifications = useNotifications();
  const markRead = useMarkNotificationsRead();

  const rows = useMemo(
    () => ownerFacing(notifications.data ?? [], businessId),
    [notifications.data, businessId],
  );

  const newest = newestScheduledFor(rows);
  const hasUnread = rows.some((row) => row.readAt === null);

  useEffect(() => {
    if (!newest || !hasUnread || markRead.isPending) return;
    markRead.mutate(newest);
    // `markRead` is a stable mutation object from TanStack Query and including it would re-run this
    // on every render of the screen it is mutating.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newest, hasUnread]);

  if (notifications.isLoading && !notifications.data) return <SkeletonList />;
  if (notifications.error && !notifications.data) {
    return <ErrorState error={notifications.error} onRetry={() => void notifications.refetch()} />;
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<AlertIcon size={30} />}
        title="Nothing to catch up on"
        message={`When a ${label("member")} cancels a booking, it shows here with the time it was for and how many spots that leaves free.`}
      />
    );
  }

  return (
    <Card>
      {rows.map((row, index) => (
        <ActivityRow key={row.id} row={row} last={index === rows.length - 1} someone={`A ${label("member")}`} />
      ))}
    </Card>
  );
}

/**
 * One event.
 *
 * The payload is read defensively. These rows are written by whichever version of the API queued
 * them, and a feed that throws because a key an older row never carried is missing would take the
 * whole screen down over one stale notification.
 */
function ActivityRow({ row, last, someone }: { row: Notification; last: boolean; someone: string }) {
  const text = (key: string, fallback: string): string => {
    const value = row.payload[key];
    return typeof value === "string" && value.length > 0 ? value : fallback;
  };
  const spotsLeft = typeof row.payload.spotsLeft === "number" ? row.payload.spotsLeft : null;
  const lateCancel = row.payload.lateCancel === true;
  const unread = row.readAt === null;

  return (
    <div
      className={[
        "flex items-start gap-3 px-4 py-3.5",
        last ? "" : "border-b border-line",
        unread ? "bg-accent-surface/40" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "mt-1.5 size-2 shrink-0 rounded-full",
          unread ? "bg-accent" : "bg-transparent",
        ].join(" ")}
      />
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold text-ink">
          {text("memberName", someone)} cancelled {text("participantName", "a place")}
        </p>
        <p className="mt-0.5 text-[13px] text-ink-2">
          {text("className", "A session")} · {text("when", "")}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {spotsLeft !== null ? (
            <Badge tone={spotsLeft === 0 ? "quiet" : "confirmed"}>
              {spotsLeft === 1 ? "1 spot free" : `${spotsLeft} spots free`}
            </Badge>
          ) : null}
          {lateCancel ? <Badge tone="late">Late cancel</Badge> : null}
          {/*
            How long ago it happened. The line above says when the session is; this says how stale
            the news is, and the two together are what decide whether the spot is still sellable.
          */}
          <span className="text-[12px] text-ink-3">{formatCountdown(row.scheduledFor)}</span>
        </div>
      </div>
    </div>
  );
}
